"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.flattenTokens = flattenTokens;
exports.toCssVarName = toCssVarName;
exports.resolveTheme = resolveTheme;
exports.renderCssVars = renderCssVars;
exports.diffTokens = diffTokens;
exports.brandOverride = brandOverride;
const tokens_1 = require("./tokens");
const layout_1 = require("./layout");
const contrast_1 = require("./contrast");
const REF_RE = /^\{([a-zA-Z0-9_.-]+)\}$/;
// 无单位令牌：字重、透明度、行高、层级。匹配路径中的关键字而非结尾，
// 因为令牌名是 camelCase（font.weightBold），结尾不可能是 weight。
const UNITLESS_RE = /(weight|opacity|lineHeight|zIndex|order|scale|flex|columns)/i;
function flattenTokens(tree, prefix = '') {
    const out = {};
    for (const [key, value] of Object.entries(tree)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value !== null && typeof value === 'object') {
            Object.assign(out, flattenTokens(value, path));
        }
        else {
            out[path] = value;
        }
    }
    return out;
}
function toCssVarName(path) {
    return `--${path.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replace(/\./g, '-')}`;
}
function toCssValue(path, value) {
    if (typeof value === 'string')
        return value;
    if (UNITLESS_RE.test(path))
        return String(value);
    return `${value}px`;
}
/** 解析 {brand.600} 引用；支持引用另一条语义令牌（最多 5 跳，防循环） */
function resolveRefs(path, value, pool, warnings, depth = 0) {
    if (typeof value !== 'string')
        return toCssValue(path, value);
    if (depth > 5) {
        warnings.push(`令牌 ${path} 的引用层级过深，可能存在循环引用`);
        return value;
    }
    const match = value.match(REF_RE);
    if (!match) {
        // 允许内联引用，例如边框 "1px solid {color.border}"
        if (!value.includes('{'))
            return value;
        return value.replace(/\{([a-zA-Z0-9_.-]+)\}/g, (whole, key) => {
            const target = pool[key];
            if (target === undefined) {
                warnings.push(`令牌 ${path} 内联引用了不存在的 ${key}`);
                return whole;
            }
            return resolveRefs(path, target, pool, warnings, depth + 1);
        });
    }
    const target = match[1];
    const next = pool[target];
    if (next === undefined) {
        warnings.push(`令牌 ${path} 引用了不存在的 ${target}`);
        return value;
    }
    return resolveRefs(path, next, pool, warnings, depth + 1);
}
function resolveTheme({ manifest, variant, overrides }) {
    const warnings = [];
    const variantKey = variant && manifest.variants[variant] ? variant : Object.keys(manifest.variants)[0];
    const variantTokens = variantKey ? manifest.variants[variantKey]?.tokens : undefined;
    const primitives = flattenTokens(manifest.tokens.primitive);
    const semantic = flattenTokens(manifest.tokens.semantic);
    const component = flattenTokens(manifest.tokens.component ?? {});
    const variantFlat = variantTokens ? flattenTokens(variantTokens) : {};
    const overrideFlat = overrides ? flattenTokens(overrides) : {};
    // 引用池：primitive + 已解析的语义键（语义键优先，便于语义令牌互相引用）
    const pool = { ...primitives, ...semantic, ...component };
    const vars = {};
    const sources = {};
    const applyLayer = (flat, source) => {
        for (const [path, value] of Object.entries(flat)) {
            const varName = toCssVarName(path);
            vars[varName] = resolveRefs(path, value, pool, warnings);
            sources[varName] = source;
        }
    };
    // 只输出 semantic + component（primitive 是调色板，组件不允许直接消费）
    applyLayer(semantic, 'semantic');
    applyLayer(component, 'semantic');
    if (variantTokens)
        applyLayer(variantFlat, 'variant');
    if (overrides)
        applyLayer(overrideFlat, 'override');
    /**
     * 组件样式槽单独一层，名字带 `--c-` 前缀，所以与上面四层不会撞车。
     *
     * 放在最后而不参与 variant 合并：变体改的是"同一套骨架里字号间距的微调"，
     * 组件槽改的是"这个组件本体的形态"（页头多高、首屏几栏）。两者的作用域
     * 不该互相覆盖 —— 合在一起以后，一条变体规则能把页头压扁，而排障时
     * 只会看到两个值里少了一个。
     */
    for (const [name, value] of Object.entries((0, layout_1.componentStyleVars)(manifest.componentStyles))) {
        vars[name] = value;
        sources[name] = 'slot';
    }
    if (variant && !manifest.variants[variant]) {
        warnings.push(`未知变体 ${variant}，已回退到 ${variantKey ?? '无'}`);
    }
    /**
     * 原型缺省 split：老主题不写这一行就还是原来的骨架，不需要改任何存量主题。
     * 类型上 `archetype` 已经是枚举，这条判断防的是**绕过类型**的调用方
     * （JSON 主题包、脚本灌进来的清单）—— 原型名打错时静默回退，
     * 症状只是"看着没描述里那么不同"，比整页渲染不出来好得多。
     */
    let archetype = tokens_1.DEFAULT_ARCHETYPE;
    if ((0, tokens_1.isThemeArchetype)(manifest.archetype)) {
        archetype = manifest.archetype;
    }
    else if (manifest.archetype) {
        warnings.push(`未知原型 ${String(manifest.archetype)}，已回退到 ${tokens_1.DEFAULT_ARCHETYPE}`);
    }
    return {
        theme: manifest.name,
        version: manifest.version,
        variant: variantKey ?? 'default',
        archetype,
        layout: manifest.layout && Object.keys(manifest.layout.pages ?? {}).length > 0 ? manifest.layout : null,
        /**
         * 能力声明跟着解析结果走，和 layout 同一个理由：站点侧只查一次表，
         * 就该同时拿到令牌、版式与"有哪些导航位置"。分开查会让两个调用方
         * 迟早读到不同版本的主题。
         */
        supports: manifest.supports ?? {},
        vars,
        sources,
        cssText: renderCssVars(vars),
        warnings,
    };
}
function renderCssVars(vars, selector = ':root') {
    const body = Object.entries(vars)
        .map(([k, v]) => `  ${k}: ${v};`)
        .join('\n');
    return `${selector} {\n${body}\n}`;
}
/**
 * 只保留与基线不同的键。客户保存设置时调用这个函数，
 * 库里的 overrides 就永远是"差异"而不是"全量快照"，主题升级才能穿透。
 */
function diffTokens(base, next) {
    const diff = {};
    for (const [k, v] of Object.entries(next)) {
        if (base[k] !== v)
            diff[k] = v;
    }
    return diff;
}
/**
 * 客户改色时最容易撞的坑：品牌色配白字对比度不足，按钮文字看不清。
 *
 * 所以**一个颜色要派生出四条令牌**，不能只换 `--color-brand`：
 * 只换主色时，hover 还是主题原来的蓝、浅底还是原来的蓝、按钮文字还是白色 ——
 * 客户挑了橙色，按钮按下去变蓝；挑了浅黄，白字直接看不见。这三处都在
 * 页面上高频出现，客户会认定"换色功能是坏的"。
 *
 * 页头强调色与品牌方块**不在这里跟进**：那是主题的设计决定
 * （meridian 的深色页头用金色下划线，是因为墨绿压上去看不见），
 * 客户的品牌色只负责"我们的颜色"，不负责"主题的层次"。
 */
function brandOverride(brandColor) {
    const rgb = (0, contrast_1.parseHex)(brandColor);
    // 解析不了就原样交给下游：悄悄换成一个"看起来差不多"的颜色，
    // 排障时没人能从结果反推库里存的是什么
    if (!rgb)
        return { color: { brand: brandColor } };
    const hex = (0, contrast_1.toHex)(rgb);
    /**
     * hover 通常是"压暗一档"，但对已经很深的品牌色（近黑）压暗等于没变，
     * 客户会以为 hover 失效。深色改成提亮，保证两种情况下都能看出反馈。
     */
    const hover = ((0, contrast_1.relativeLuminance)(rgb) < 0.06 ? (0, contrast_1.mixHex)(hex, '#ffffff', 0.16) : (0, contrast_1.mixHex)(hex, '#000000', 0.16)) ??
        hex;
    // 8% 的品牌色 + 92% 白：标签底色与首屏渐变都用它，浓了会盖过正文
    const subtle = (0, contrast_1.mixHex)(hex, '#ffffff', 0.92) ?? hex;
    return {
        color: {
            brand: hex,
            brandHover: hover,
            brandSubtle: subtle,
            // 白字还是深字由对比度算出来：浅色品牌配白字就是不可读，不是"风格问题"
            onBrand: (0, contrast_1.suggestOnBrand)(hex),
        },
    };
}
//# sourceMappingURL=resolve.js.map